/**
 * Model-facing company-identity vocabulary for `id_lookup`.
 *
 * FactSet's lookup API filters public listings with vendor codes such as
 * `EQ_DR_ADR` and `US`. Those codes are an internal wire contract. The agent
 * sees semantic names, and this catalogue is the only place that translates
 * them. FactSet's legacy `EU → United Kingdom` region code is intentionally
 * absent because `united_kingdom → GB` is unambiguous.
 */

/** One public-equity security class the agent can request. */
export interface IdLookupSecurityTypeDefinition {
  /** Semantic enum value exposed in the tool schema. */
  readonly name: string;
  /** FactSet `equityType` wire value sent to `/idsearch/v2/lookup`. */
  readonly wireValue: string;
  /** Plain-language meaning included in the tool description. */
  readonly description: string;
}

/** One FactSet listing region the agent can request. */
export interface IdLookupListingRegionDefinition {
  /** Semantic enum value exposed in the tool schema. */
  readonly name: string;
  /** FactSet ISO-style listing-region code. */
  readonly wireValue: string;
}

/**
 * Public-equity security types accepted by FactSet ID Lookup.
 *
 * `EQUITY` and `EQUITY_DEFAULT` exist in older internal allowlists but are not
 * documented in the FactSet user guide, so they are not exposed here.
 */
export const ID_LOOKUP_SECURITY_TYPES = [
  {
    name: "primary_market_equity",
    wireValue: "EQ_PRIMARY",
    description: "the issuer's primary-market equity",
  },
  {
    name: "preferred_equity",
    wireValue: "EQ_PREF",
    description: "preferred shares",
  },
  {
    name: "rights",
    wireValue: "EQ_RIGHTS",
    description: "subscription or shareholder rights",
  },
  {
    name: "warrant",
    wireValue: "EQ_WARR",
    description: "an equity warrant",
  },
  {
    name: "depositary_receipt",
    wireValue: "EQ_DEPOS",
    description: "a generic depositary receipt",
  },
  {
    name: "american_depositary_receipt",
    wireValue: "EQ_DR_ADR",
    description: "an American depositary receipt",
  },
  {
    name: "global_depositary_receipt",
    wireValue: "EQ_DR_GDR",
    description: "a global depositary receipt",
  },
  {
    name: "non_voting_depositary_receipt",
    wireValue: "EQ_DR_NVDR",
    description: "a non-voting depositary receipt",
  },
  {
    name: "other_depositary_receipt",
    wireValue: "EQ_DR_ODR",
    description: "another depositary-receipt class",
  },
] as const satisfies readonly IdLookupSecurityTypeDefinition[];

/** Semantic security-type names exposed to the model. */
export const ID_LOOKUP_SECURITY_TYPE_NAMES = ID_LOOKUP_SECURITY_TYPES.map(
  (entry) => entry.name,
);

const SECURITY_TYPE_WIRE_BY_NAME = new Map<string, string>(
  ID_LOOKUP_SECURITY_TYPES.map((entry) => [entry.name, entry.wireValue]),
);

/**
 * Complete FactSet listing-region catalogue from the ID Lookup user guide.
 *
 * These identify where a security is listed. They do not identify the company's
 * domicile.
 */
export const ID_LOOKUP_LISTING_REGIONS = [
  ["afghanistan", "AF"],
  ["albania", "AL"],
  ["algeria", "DZ"],
  ["andorra", "AD"],
  ["angola", "AO"],
  ["anguilla", "AI"],
  ["antigua_and_barbuda", "AG"],
  ["argentina", "AR"],
  ["armenia", "AM"],
  ["aruba", "AW"],
  ["australia", "AU"],
  ["austria", "AT"],
  ["azerbaijan", "AZ"],
  ["bahamas", "BS"],
  ["bahrain", "BH"],
  ["bangladesh", "BD"],
  ["barbados", "BB"],
  ["belarus", "BY"],
  ["belgium", "BE"],
  ["belize", "BZ"],
  ["bermuda", "BM"],
  ["bhutan", "BT"],
  ["bolivia", "BO"],
  ["bosnia_and_herzegovina", "BA"],
  ["botswana", "BW"],
  ["brazil", "BR"],
  ["bulgaria", "BG"],
  ["cambodia", "KH"],
  ["cameroon", "CM"],
  ["canada", "CA"],
  ["cape_verde", "CV"],
  ["cayman_islands", "KY"],
  ["chad", "TD"],
  ["chile", "CL"],
  ["china", "CN"],
  ["colombia", "CO"],
  ["cook_islands", "CK"],
  ["costa_rica", "CR"],
  ["croatia", "HR"],
  ["curacao", "CW"],
  ["cyprus", "CY"],
  ["czech_republic", "CZ"],
  ["denmark", "DK"],
  ["dominican_republic", "DO"],
  ["ecuador", "EC"],
  ["egypt", "EG"],
  ["el_salvador", "SV"],
  ["estonia", "EE"],
  ["faroe_islands", "FO"],
  ["fiji", "FJ"],
  ["finland", "FI"],
  ["france", "FR"],
  ["georgia", "GE"],
  ["germany", "DE"],
  ["ghana", "GH"],
  ["gibraltar", "GI"],
  ["greece", "GR"],
  ["guatemala", "GT"],
  ["guernsey", "GG"],
  ["guyana", "GY"],
  ["honduras", "HN"],
  ["hong_kong", "HK"],
  ["hungary", "HU"],
  ["iceland", "IS"],
  ["india", "IN"],
  ["indonesia", "ID"],
  ["iran", "IR"],
  ["iraq", "IQ"],
  ["ireland", "IE"],
  ["isle_of_man", "IM"],
  ["israel", "IL"],
  ["italy", "IT"],
  ["ivory_coast", "CI"],
  ["jamaica", "JM"],
  ["japan", "JP"],
  ["jersey", "JE"],
  ["jordan", "JO"],
  ["kazakhstan", "KZ"],
  ["kenya", "KE"],
  ["south_korea", "KR"],
  ["kuwait", "KW"],
  ["kyrgyzstan", "KG"],
  ["laos", "LA"],
  ["latvia", "LV"],
  ["lebanon", "LB"],
  ["liberia", "LR"],
  ["libya", "LY"],
  ["liechtenstein", "LI"],
  ["lithuania", "LT"],
  ["luxembourg", "LU"],
  ["macao", "MO"],
  ["north_macedonia", "MK"],
  ["madagascar", "MG"],
  ["malawi", "MW"],
  ["malaysia", "MY"],
  ["maldives", "MV"],
  ["malta", "MT"],
  ["marshall_islands", "MH"],
  ["mauritius", "MU"],
  ["mexico", "MX"],
  ["moldova", "MD"],
  ["monaco", "MC"],
  ["mongolia", "MN"],
  ["montenegro", "ME"],
  ["morocco", "MA"],
  ["mozambique", "MZ"],
  ["myanmar", "MM"],
  ["namibia", "NA"],
  ["nepal", "NP"],
  ["new_caledonia", "NC"],
  ["new_zealand", "NZ"],
  ["nicaragua", "NI"],
  ["nigeria", "NG"],
  ["norway", "NO"],
  ["oman", "OM"],
  ["pakistan", "PK"],
  ["palestine", "PS"],
  ["panama", "PA"],
  ["papua_new_guinea", "PG"],
  ["paraguay", "PY"],
  ["peru", "PE"],
  ["philippines", "PH"],
  ["poland", "PL"],
  ["portugal", "PT"],
  ["puerto_rico", "PR"],
  ["qatar", "QA"],
  ["romania", "RO"],
  ["russia", "RU"],
  ["rwanda", "RW"],
  ["saint_kitts_and_nevis", "KN"],
  ["saint_lucia", "LC"],
  ["saint_vincent_and_the_grenadines", "VC"],
  ["saudi_arabia", "SA"],
  ["serbia", "RS"],
  ["seychelles", "SC"],
  ["singapore", "SG"],
  ["slovakia", "SK"],
  ["slovenia", "SI"],
  ["south_africa", "ZA"],
  ["spain", "ES"],
  ["sri_lanka", "LK"],
  ["sudan", "SD"],
  ["eswatini", "SZ"],
  ["sweden", "SE"],
  ["switzerland", "CH"],
  ["syria", "SY"],
  ["taiwan", "TW"],
  ["tajikistan", "TJ"],
  ["tanzania", "TZ"],
  ["thailand", "TH"],
  ["netherlands", "NL"],
  ["trinidad_and_tobago", "TT"],
  ["tunisia", "TN"],
  ["turkey", "TR"],
  ["turks_and_caicos_islands", "TC"],
  ["uganda", "UG"],
  ["ukraine", "UA"],
  ["united_arab_emirates", "AE"],
  ["united_kingdom", "GB"],
  ["united_states_minor_outlying_islands", "UM"],
  ["united_states", "US"],
  ["uruguay", "UY"],
  ["uzbekistan", "UZ"],
  ["vanuatu", "VU"],
  ["venezuela", "VE"],
  ["vietnam", "VN"],
  ["british_virgin_islands", "VG"],
  ["united_states_virgin_islands", "VI"],
  ["yemen", "YE"],
  ["zambia", "ZM"],
  ["zimbabwe", "ZW"],
] as const satisfies readonly (readonly [string, string])[];

/** Semantic listing-region names exposed to the model. */
export const ID_LOOKUP_LISTING_REGION_NAMES = ID_LOOKUP_LISTING_REGIONS.map(
  ([name]) => name,
);

const LISTING_REGION_WIRE_BY_NAME = new Map<string, string>(
  ID_LOOKUP_LISTING_REGIONS.map(([name, wireValue]) => [name, wireValue]),
);

/**
 * Translate one semantic security type to its FactSet wire value.
 *
 * @param name - A value from {@link ID_LOOKUP_SECURITY_TYPE_NAMES}.
 * @returns The FactSet `equityType` code.
 * @throws {Error} When `name` is not in the catalogue.
 */
export function securityTypeWireValue(name: string): string {
  const wireValue = SECURITY_TYPE_WIRE_BY_NAME.get(name);
  if (!wireValue) {
    throw new Error(`Unsupported id_lookup security type "${name}".`);
  }
  return wireValue;
}

/**
 * Translate one semantic listing region to its FactSet wire value.
 *
 * @param name - A value from {@link ID_LOOKUP_LISTING_REGION_NAMES}.
 * @returns The FactSet listing-region code.
 * @throws {Error} When `name` is not in the catalogue.
 */
export function listingRegionWireValue(name: string): string {
  const wireValue = LISTING_REGION_WIRE_BY_NAME.get(name);
  if (!wireValue) {
    throw new Error(`Unsupported id_lookup listing region "${name}".`);
  }
  return wireValue;
}

/**
 * Describe every exposed security type in one sentence for the tool schema.
 *
 * @returns Guidance that defines each semantic security-type name.
 */
export function describeIdLookupSecurityTypes(): string {
  return ID_LOOKUP_SECURITY_TYPES.map(
    (entry) => `${entry.name} means ${entry.description}`,
  ).join("; ");
}
